import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { toast } from "react-toastify";
import { ENDPOINT_BASE } from "../api";
import useMyService from "../service/useMyService";
import { useQueryGetUserByKeycloakId } from "./user/useQueryGetUserByKeycloakId";

export function useQueryCart() {
  const { getEntityById } = useMyService();

  const { userId } = useQueryGetUserByKeycloakId();
  const { mutateAsync } = useAddEditCart();

  const {
    data: cart,
    isError,
    error,
    isLoading,
    isSuccess,
  } = useQuery({
    queryKey: ["cart"],
    queryFn: ({ signal }) =>
      getEntityById({
        endpoint: ENDPOINT_BASE.CART + "/byUserId",
        id: userId,
        signal,
      }),
    enabled: !!userId,
    retry: false,
  });

  useEffect(() => {
    if (isError) {
      toast.error("An error occurred while fetching cart.");
    }
  }, [isError, error]);

  useEffect(() => {
    if (!cart || !userId) return;

    // Create cart if not exists
    if (!cart.length) {
      mutateAsync({
        dataObj: {
          user: {
            id: userId,
          },
        },
      });
    }
  }, [cart, userId]);

  return {
    cart: cart?.[0],
    isLoading,
    isSuccess,
  };
}

export function useAddEditCart() {
  const { addEntity, updateEntity } = useMyService();
  const queryClient = useQueryClient();

  const { isSuccess, mutateAsync, isError, error, isPending } = useMutation({
    mutationFn: ({ dataObj, signal }) =>
      dataObj.id
        ? updateEntity({ dataObj, endpoint: ENDPOINT_BASE.CART, signal })
        : addEntity({ dataObj, endpoint: ENDPOINT_BASE.CART, signal }),
  });

  useEffect(() => {
    if (isSuccess) queryClient.invalidateQueries(["cart"]);
  }, [isSuccess, queryClient]);

  useEffect(() => {
    if (isError) {
      toast.error("An error occurred while adding/editing cart.");
    }
  }, [isError, error]);

  return {
    mutateAsync,
    isPending,
  };
}
